import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek,
         endOfWeek, isSameMonth, isToday, addMonths, subMonths, getDay } from 'date-fns';
import { el } from 'date-fns/locale';
import { appointmentsCalendarAPI } from '../services/api';
import { PageHeader, ActionBtn } from '../components/Shared';
import toast from 'react-hot-toast';
import styles from './Pages.module.css';

// ROLE SEPARATION: operational data only. consultation_outcome is billing
// relevant. Clinical outcomes are not exposed to admin -- do not add them.

const DAY_NAMES = ['ΔΕΥ', 'ΤΡΙ', 'ΤΕΤ', 'ΠΕΜ', 'ΠΑΡ', 'ΣΑΒ', 'ΚΥΡ'];

const STATUS_EL = {
  scheduled: 'Προγραμματισμένο',
  in_progress: 'Σε εξέλιξη',
  completed: 'Ολοκληρωμένο',
  cancelled_by_doctor: 'Ακύρωση (ιατρός)',
  cancelled_by_patient: 'Ακύρωση (ασθενής)',
  cancelled_by_admin: 'Ακύρωση (διαχείριση)',
  no_show: 'Μη προσέλευση',
};

const STATUS_COLOR = {
  scheduled: '#2563EB',
  in_progress: '#E07B2A',
  completed: '#0D9A6A',
  cancelled_by_doctor: '#DC2626',
  cancelled_by_patient: '#DC2626',
  cancelled_by_admin: '#DC2626',
  no_show: '#7C3AED',
};

const OUTCOME_EL = {
  completed: 'Ολοκληρώθηκε',
  dna: 'Δεν προσήλθε ο ασθενής',
  abandoned: 'Διακόπηκε',
  doctor_no_show: 'Απουσία ιατρού',
};

const Pill = ({ text, color }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
    fontWeight: 700, color, background: color + '18', whiteSpace: 'nowrap',
  }}>{text}</span>
);

export default function AppointmentsCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [days, setDays] = useState({});
  const [totals, setTotals] = useState({ appointments: 0, stale: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayRows, setDayRows] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(false);

  const loadMonth = async () => {
    setLoading(true);
    try {
      const { data } = await appointmentsCalendarAPI.getMonth(
        currentDate.getFullYear(), currentDate.getMonth() + 1, includeDemo);
      setDays(data.data.days || {});
      setTotals(data.data.totals || { appointments: 0, stale: 0 });
    } catch (e) {
      toast.error('Αδυναμία φόρτωσης ημερολογίου');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    loadMonth(); setSelectedDay(null); setDayRows([]);
  // @m24-deps: dependency list left exactly as it was when linting was switched on 25 Sep 2026.
  // Adding the missing dependency can change how often this effect runs, so it is a behaviour
  // change to make deliberately and test, not a lint tidy-up. Recorded, not silenced by turning
  // the rule off - it still guards every effect written from here on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, includeDemo]);

  const loadDay = async (key) => {
    setSelectedDay(key); setDayLoading(true);
    try {
      const { data } = await appointmentsCalendarAPI.getDay(key, includeDemo);
      setDayRows(data.data.appointments || []);
    } catch (e) {
      toast.error('Αδυναμία φόρτωσης ημέρας'); setDayRows([]);
    } finally { setDayLoading(false); }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calDays = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(monthEnd, { weekStartsOn: 1 }),
  });

  const nav = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                      color: 'var(--muted)', cursor: 'pointer', marginRight: 4 }}>
        <input type="checkbox" checked={includeDemo}
               onChange={e => setIncludeDemo(e.target.checked)} />
        Demo
      </label>
      <ActionBtn onClick={() => setCurrentDate(d => subMonths(d, 1))}>‹ Προηγ.</ActionBtn>
      <ActionBtn onClick={() => setCurrentDate(new Date())}>Σήμερα</ActionBtn>
      <ActionBtn onClick={() => setCurrentDate(d => addMonths(d, 1))}>Επόμ. ›</ActionBtn>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={'Ραντεβού — ' + format(currentDate, 'MMMM yyyy', { locale: el })}
        subtitle={totals.appointments + ' ραντεβού τον μήνα'
                  + (totals.stale > 0 ? ' · ' + totals.stale + ' εκκρεμή προς τακτοποίηση' : '')}
        action={nav} />

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '0 0 12px',
                    fontSize: 12, color: 'var(--muted)', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: '#0D9A6A' }} />Ολοκληρωμένα</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: '#2563EB' }} />Προγραμματισμένα</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: '#DC2626' }} />Ακυρωμένα</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: '#E07B2A', fontWeight: 700 }}>▲</span>Εκκρεμή (δεν έκλεισαν)</span>
      </div>

      {loading ? <div className={styles.loading}>Φόρτωση...</div> : (
        <div className={styles.rotaCalendar}>
          <div className={styles.rotaHead}>
            {DAY_NAMES.map(d => <div key={d} className={styles.rotaHeadDay}>{d}</div>)}
          </div>
          <div className={styles.rotaBody}>{calDays.map(day => {
            const key = format(day, 'yyyy-MM-dd');
            const info = days[key];
            const inMonth = isSameMonth(day, currentDate);
            const todayCheck = isToday(day);
            const wd = getDay(day);
            const weekend = wd === 0 || wd === 6;
            const isSel = selectedDay === key;
            let bg = 'transparent';
            if (inMonth) bg = info ? '#FFFFFF' : (weekend ? 'var(--bg)' : 'transparent');
            return (
              <div key={key}
                   onClick={() => inMonth && info && loadDay(key)}
                   className={[styles.rotaCell, !inMonth && styles.otherMonth,
                               todayCheck && styles.today].filter(Boolean).join(' ')}
                   style={{ cursor: inMonth && info ? 'pointer' : 'default', background: bg,
                            outline: isSel ? '2px solid var(--crimson)' : 'none' }}>
                <div className={styles.rotaCellDate}>
                  {todayCheck ? <span className={styles.todayBadge}>{format(day, 'd')}</span>
                              : format(day, 'd')}
                </div>
                {inMonth && info && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
                      <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)' }}>{info.total}</span>
                      <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                        {info.total === 1 ? 'ραντεβού' : 'ραντεβού'}
                      </span>
                      {info.stale > 0 && <span style={{ fontSize: 11, color: '#E07B2A',
                                                        fontWeight: 800 }} title="Εκκρεμή">▲</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' }}>
                      {info.completed > 0 && <Pill text={info.completed} color="#0D9A6A" />}
                      {info.scheduled > 0 && <Pill text={info.scheduled} color="#2563EB" />}
                      {info.in_progress > 0 && <Pill text={info.in_progress} color="#E07B2A" />}
                      {info.cancelled > 0 && <Pill text={info.cancelled} color="#DC2626" />}
                    </div>
                  </>
                )}
              </div>
            );
          })}</div>
        </div>
      )}

      {selectedDay && (
        <div style={{ background: 'white', border: '1px solid var(--border)',
                      borderRadius: 12, padding: 16, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 14 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>
              {format(new Date(selectedDay + 'T00:00:00'), 'EEEE, d MMMM yyyy', { locale: el })}
            </span>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {dayRows.length} {dayRows.length === 1 ? 'ραντεβού' : 'ραντεβού'}
            </span>
          </div>

          {dayLoading ? <div className={styles.loading}>Φόρτωση...</div>
           : dayRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)', fontSize: 13 }}>
              Κανένα ραντεβού για αυτή την ημέρα.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: 11 }}>
                    <th style={{ padding: '6px 8px' }}>ΩΡΑ</th>
                    <th style={{ padding: '6px 8px' }}>ΑΣΘΕΝΗΣ</th>
                    <th style={{ padding: '6px 8px' }}>ΙΑΤΡΟΣ</th>
                    <th style={{ padding: '6px 8px' }}>ΚΑΤΑΣΤΑΣΗ</th>
                    <th style={{ padding: '6px 8px' }}>ΕΚΒΑΣΗ</th>
                    <th style={{ padding: '6px 8px' }}>ΚΩΔ.</th>
                  </tr>
                </thead>
                <tbody>
                  {dayRows.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--border)',
                                            background: r.is_stale ? '#FFF6EC' : 'transparent' }}>
                      <td style={{ padding: '8px', fontWeight: 700, color: 'var(--navy)',
                                   whiteSpace: 'nowrap' }}>
                        {r.is_stale && <span style={{ color: '#E07B2A', marginRight: 4 }}
                                             title="Δεν έκλεισε">▲</span>}
                        {r.start_time?.slice(0, 5)}–{r.end_time?.slice(0, 5)}
                      </td>
                      <td style={{ padding: '8px' }}>{r.patient_name || '—'}</td>
                      <td style={{ padding: '8px' }}>
                        {r.doctor_name || '—'}
                        {r.doctor_is_demo && <span style={{ fontSize: 10, color: 'var(--muted)',
                                                            marginLeft: 6 }}>demo</span>}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Pill text={STATUS_EL[r.status] || r.status}
                              color={STATUS_COLOR[r.status] || '#666'} />
                      </td>
                      <td style={{ padding: '8px', color: r.consultation_outcome ? 'var(--navy)'
                                                                                 : 'var(--muted)' }}>
                        {r.consultation_outcome
                          ? (OUTCOME_EL[r.consultation_outcome] || r.consultation_outcome)
                          : '—'}
                      </td>
                      <td style={{ padding: '8px', color: 'var(--muted)', fontSize: 11,
                                   whiteSpace: 'nowrap' }}>
                        {r.consultation_number || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
